/**
 * Serviço para gerenciar clientes
 */

import { getToken } from "./tokenService";
import { API_BASE_URL } from "@/lib/apiConfig";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: string;
  };
  message?: string;
}

export interface ProductInfo {
  id: string;
  name: string;
  description: string;
  status_name?: string;
}

export interface ClientStatus {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

export interface ClientPlanSummary {
  assignment_type: "catalog" | "pending" | "enterprise";
  plan_id: string | null;
  plan_name: string | null;
  display_label: string;
  monthly_call_limit?: number | string | null;
  monthly_token_limit?: number | string | null;
  excess_rate_per_1m_tokens?: number | string | null;
}

export interface ClientPlanInfo {
  id: string | null;
  name: string;
  monthly_call_limit?: number | string;
  monthly_token_limit?: number | string;
  excess_rate_per_1m_tokens?: number | string;
}

export interface Client {
  id: string;
  identifier: string;
  name: string;
  email: string;
  company: string;
  description?: string;
  status_id: number;
  status_name: string;
  status_description?: string;
  status_is_active?: boolean;
  status: ClientStatus;
  plan_id?: string | null;
  plan?: ClientPlanInfo | null;
  plan_summary?: ClientPlanSummary | null;
  created_at: string;
  updated_at: string;
  products: ProductInfo[];
  product_count: number;
}

export interface ClientsListResponse {
  clients: Client[];
  total: number;
  pagination: {
    limit: number;
    offset: number;
    total: number;
    pages: number;
  };
}

export interface CreateClientRequest {
  identifier: string;
  name: string;
  email: string;
  company: string;
  description?: string;
  status_id: number;
  product_ids?: string[];
  plan_id?: string | null;
  plan_assignment_type?: "catalog" | "pending" | "enterprise";
  monthly_call_limit?: number | null;
  monthly_token_limit?: number | null;
  excess_rate_per_1m_tokens?: number | null;
}

export interface UpdateClientRequest {
  identifier?: string;
  name?: string;
  email?: string;
  company?: string;
  description?: string;
  status_id?: number;
  product_ids?: string[];
  plan_id?: string | null;
  plan_assignment_type?: "catalog" | "pending" | "enterprise";
  monthly_call_limit?: number | null;
  monthly_token_limit?: number | null;
  excess_rate_per_1m_tokens?: number | null;
}

export interface ClientsStats {
  total_clients: number;
  clients_by_status: Array<{ status_name: string; count: string }>;
  top_clients_by_products: Array<{
    client_name: string;
    company: string;
    product_count: string;
  }>;
}

/** Igual à mensagem de conflito retornada pela API ao salvar. */
export const CLIENT_DUPLICATE_COMPANY_MESSAGE =
  "Já existe um cliente cadastrado com este nome de empresa";

export interface ClientCompanyAvailabilityData {
  available: boolean;
}

/**
 * Faz uma requisição autenticada para a API
 */
async function apiRequest<T>(
  endpoint: string,
  method: string = "GET",
  body?: any,
): Promise<ApiResponse<T>> {
  const token = getToken();

  if (!token) {
    throw new Error("Token de autenticação não encontrado");
  }

  const config: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  if (body && method !== "GET") {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || {
          code: "HTTP_ERROR",
          message: `Erro HTTP: ${response.status}`,
        },
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      error: {
        code: "NETWORK_ERROR",
        message:
          error instanceof Error ? error.message : "Erro de conexão com a API",
      },
    };
  }
}

/**
 * Lista clientes com filtros e paginação
 */
export async function listClients(options?: {
  limit?: number;
  offset?: number;
  status_id?: number;
  search?: string;
}): Promise<ApiResponse<ClientsListResponse>> {
  const params = new URLSearchParams();

  if (options?.limit) params.append("limit", options.limit.toString());
  if (options?.offset) params.append("offset", options.offset.toString());
  if (options?.status_id)
    params.append("status_id", options.status_id.toString());
  if (options?.search) params.append("search", options.search);

  const queryString = params.toString();
  return apiRequest<ClientsListResponse>(
    `/clients${queryString ? `?${queryString}` : ""}`,
  );
}

/**
 * Obtém um cliente por ID
 */
export async function getClientById(id: string): Promise<ApiResponse<Client>> {
  return apiRequest<Client>(`/clients/${id}`);
}

/**
 * Verifica disponibilidade do nome da empresa antes do envio do formulário.
 *
 * @param company Valor atual do campo empresa
 * @param options `excludeClientId` na edição; `signal` para cancelar requisições antigas
 *
 * @returns Payload com `available` ou erro de rede/HTTP
 *
 * @example
 * ```typescript
 * const res = await getClientCompanyAvailability("Acme", {
 *   excludeClientId: editingId,
 *   signal: controller.signal,
 * });
 * ```
 */
export async function getClientCompanyAvailability(
  company: string,
  options?: { excludeClientId?: string | null; signal?: AbortSignal | null },
) {
  const token = getToken();

  if (!token) {
    throw new Error("Token de autenticação não encontrado");
  }

  const params = new URLSearchParams();
  params.append("company", company);
  if (options?.excludeClientId) {
    params.append("exclude_client_id", options.excludeClientId);
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/clients/company-availability?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        signal: options?.signal ?? null,
      },
    );
    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || {
          code: "HTTP_ERROR",
          message: `Erro HTTP: ${response.status}`,
        },
      };
    }

    return data as ApiResponse<ClientCompanyAvailabilityData>;
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw error;
    }

    return {
      success: false,
      error: {
        code: "NETWORK_ERROR",
        message:
          error instanceof Error ? error.message : "Erro de conexão com a API",
      },
    };
  }
}

/**
 * Cria um novo cliente
 */
export async function createClient(
  client: CreateClientRequest,
): Promise<ApiResponse<Client>> {
  return apiRequest<Client>("/clients", "POST", client);
}

/**
 * Atualiza um cliente
 */
export async function updateClient(
  id: string,
  client: UpdateClientRequest,
): Promise<ApiResponse<Client>> {
  return apiRequest<Client>(`/clients/${id}`, "PUT", client);
}

/**
 * Remove um cliente
 */
export async function deleteClient(id: string): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/clients/${id}`, "DELETE");
}

/**
 * Altera o status de um cliente
 */
export async function changeClientStatus(
  id: string,
  statusId: number,
): Promise<ApiResponse<Client>> {
  return apiRequest<Client>(`/clients/${id}/status`, "PATCH", {
    status_id: statusId,
  });
}

/**
 * Associa um produto a um cliente
 */
export async function associateProductToClient(
  clientId: string,
  productId: string,
): Promise<ApiResponse<Client>> {
  return apiRequest<Client>(`/clients/${clientId}/products`, "POST", {
    product_id: productId,
  });
}

/**
 * Remove a associação de um produto com um cliente
 */
export async function dissociateProductFromClient(
  clientId: string,
  productId: string,
): Promise<ApiResponse<void>> {
  return apiRequest<void>(
    `/clients/${clientId}/products/${productId}`,
    "DELETE",
  );
}

/**
 * Obtém estatísticas dos clientes
 */
export async function getClientsStats(): Promise<ApiResponse<ClientsStats>> {
  return apiRequest<ClientsStats>("/clients/stats");
}

/**
 * Lista os tipos de status de clientes
 */
export interface ClientStatusType {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

export async function listClientStatusTypes(): Promise<
  ApiResponse<ClientStatusType[]>
> {
  return apiRequest<ApiResponse<ClientStatusType[]>>("/types/client-status");
}

/**
 * Interface para cliente simples (para filtros)
 */
export interface AccessibleClient {
  id: string;
  identifier: string;
  name: string;
  email: string;
  company: string;
}

export interface AccessibleClientsResponse {
  clients: AccessibleClient[];
  is_admin: boolean;
}

/**
 * Lista clientes acessíveis pelo usuário para filtros
 */
export async function getAccessibleClients(): Promise<
  ApiResponse<AccessibleClientsResponse>
> {
  return apiRequest<AccessibleClientsResponse>("/clients/accessible");
}
